import React, { useEffect, useMemo, useRef, useState } from 'react';
import './ChatbotWidget.css';

const DEFAULT_SYSTEM_PROMPT =
  "You are C2P Engineering Solutions support assistant. Keep replies short, professional, and helpful. If the user asks for a quote/estimate, ask for: product/service, material, quantity, drawings (if any), timeline, and contact details. If you don't know something, ask a clarifying question.";

const quickActions = [
  { label: 'Solutions Info', message: 'I need solutions information.' },
  { label: 'Get an Estimate', message: 'I would like an estimate.' },
  { label: 'Support', message: 'I need support.' },
  { label: 'Contact Sales', message: 'I want to talk to sales.' },
];

const ChatbotWidget = ({ isOpen, onClose, draftMessage, onDraftConsumed }) => {
  const systemPrompt = useMemo(
    () => process.env.REACT_APP_CHAT_SYSTEM_PROMPT || DEFAULT_SYSTEM_PROMPT,
    []
  );

  const [messages, setMessages] = useState(() => [
    {
      id: 'welcome',
      role: 'assistant',
      content: 'Hello! Welcome to C2P Engineering. How can I help you today?',
      ts: Date.now(),
    },
  ]);
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState('');

  const abortRef = useRef(null);

  const chatApiUrl = useMemo(
    () => process.env.REACT_APP_CHAT_API_URL || '/api/chat',
    []
  );

  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    // Focus input when opened
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const draft = (draftMessage || '').trim();
    if (!draft) return;
    setInput((prev) => (prev.trim() ? prev : draft));
    onDraftConsumed?.();
  }, [draftMessage, isOpen, onDraftConsumed]);

  useEffect(() => {
    if (!isOpen) return;
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, isOpen]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (!isOpen) return;
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    // If the widget is closed, abort any pending request and clear transient error.
    if (isOpen) return;
    abortRef.current?.abort?.();
    abortRef.current = null;
    setIsSending(false);
    setError('');
  }, [isOpen]);

  const sendMessage = async (text) => {
    const trimmed = (text || '').trim();
    if (!trimmed || isSending) return;

    setError('');
    setIsSending(true);

    // Cancel any previous request.
    abortRef.current?.abort?.();
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    abortRef.current = controller;

    const userMsg = {
      id: `u_${Date.now()}`,
      role: 'user',
      content: trimmed,
      ts: Date.now(),
    };

    let historyForRequest;
    setMessages((prev) => {
      historyForRequest = [...prev, userMsg]
        .filter((m) => m.role === 'user' || m.role === 'assistant')
        .map((m) => ({ role: m.role, content: m.content }));
      return [...prev, userMsg];
    });
    setInput('');

    try {
      const res = await fetch(chatApiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ systemPrompt, messages: historyForRequest || [] }),
        signal: controller?.signal,
      });

      if (!res.ok) {
        const maybe = await res.json().catch(() => null);
        throw new Error(maybe?.error || `Request failed (${res.status})`);
      }

      const data = await res.json();
      const answer = (data?.message || '').trim();

      setMessages((prev) => [
        ...prev,
        {
          id: `a_${Date.now()}`,
          role: 'assistant',
          content: answer || "Sorry – I couldn't generate a response.",
          ts: Date.now(),
        },
      ]);
    } catch (e) {
      if (e?.name === 'AbortError') return;
      setError(e?.message || 'Failed to send message.');
    } finally {
      setIsSending(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const onQuickAction = (message) => {
    sendMessage(message);
  };

  if (!isOpen) return null;

  return (
    <div className="chatbot-overlay" role="dialog" aria-modal="false" aria-label="Chat">
      <div className="chatbot-panel">
        <div className="chatbot-header">
          <div className="chatbot-title">
            <div className="chatbot-avatar" aria-hidden="true">
              <svg
                className="chatbot-robot"
                width="22"
                height="22"
                viewBox="0 0 24 24"
                aria-hidden="true"
                focusable="false"
              >
                <path
                  fill="currentColor"
                  d="M11 2h2v2h-2V2Zm-2 4h6a4 4 0 0 1 4 4v8a4 4 0 0 1-4 4H9a4 4 0 0 1-4-4v-8a4 4 0 0 1 4-4Zm0 2a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2H9Zm0 3h2v2H9v-2Zm4 0h2v2h-2v-2Zm-4 4h6v2H9v-2Z"
                />
              </svg>
            </div>
            <div>
              <div className="chatbot-name">C2P Support</div>
              <div className="chatbot-status">
                <span className="chatbot-status-dot" aria-hidden="true" /> Online
              </div>
            </div>
          </div>

          <div className="chatbot-actions">
            <button
              type="button"
              className="chatbot-icon-btn"
              aria-label="Clear chat"
              title="Clear chat"
              onClick={() => {
                abortRef.current?.abort?.();
                abortRef.current = null;
                setIsSending(false);
                setError('');
                setMessages([
                  {
                    id: 'welcome',
                    role: 'assistant',
                    content: 'Hello! Welcome to C2P Engineering. How can I help you today?',
                    ts: Date.now(),
                  },
                ]);
              }}
            >
              <svg
                className="chatbot-icon"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                aria-hidden="true"
                focusable="false"
              >
                <path
                  fill="currentColor"
                  d="M9 3h6l1 2h4v2H4V5h4l1-2Zm1 7h2v9h-2v-9Zm4 0h2v9h-2v-9ZM7 10h2v9H7v-9Zm-1 12h12a2 2 0 0 0 2-2V8H4v12a2 2 0 0 0 2 2Z"
                />
              </svg>
            </button>
            <button
              type="button"
              className="chatbot-icon-btn"
              aria-label="Close chat"
              title="Close chat"
              onClick={onClose}
            >
              <svg
                className="chatbot-icon"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                aria-hidden="true"
                focusable="false"
              >
                <path
                  fill="currentColor"
                  d="M18.3 5.71 12 12l6.3 6.29-1.41 1.42L10.59 13.4 4.29 19.71 2.88 18.29 9.17 12 2.88 5.71 4.29 4.29l6.3 6.3 6.29-6.3 1.42 1.42Z"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className="chatbot-body" ref={scrollRef}>
          {messages.map((m) => (
            <div
              key={m.id}
              className={`chatbot-row ${m.role === 'user' ? 'is-user' : 'is-bot'}`}
            >
              <div className="chatbot-bubble">{m.content}</div>
            </div>
          ))}

          {error ? <div className="chatbot-error">{error}</div> : null}
          {isSending ? <div className="chatbot-typing">Typing…</div> : null}
        </div>

        <div className="chatbot-quick">
          {quickActions.map((a) => (
            <button
              key={a.label}
              type="button"
              className="chatbot-chip"
              onClick={() => onQuickAction(a.message)}
              disabled={isSending}
            >
              {a.label}
            </button>
          ))}
        </div>

        <form className="chatbot-input" onSubmit={onSubmit}>
          <input
            ref={inputRef}
            type="text"
            placeholder="Type your message..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={isSending}
            aria-label="Message"
          />
          <button type="submit" disabled={isSending || !input.trim()} aria-label="Send">
            ➤
          </button>
        </form>

        <div className="chatbot-footer">Powered by C2P</div>
      </div>
    </div>
  );
};

export default ChatbotWidget;
